var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useReducer } from 'react';
import { checkSelectedFilterApplied, verifySelectedPortFilter } from './port-filter-utils';
import { usePortSmartgroups } from '@/api/ports/smartgroups';
var PortFilterContext = createContext(null);
/**
 * Port Filter Context Consumer
 */
export function usePortFilterContext() {
    var context = useContext(PortFilterContext);
    if (!context) {
        throw new Error('usePortFilterContext must be used within a PortFilterContextProvider');
    }
    return context;
}
/**
 * Selected Port Filter Reducer
 */
var selectedPortFilterReducer = function (state, action) {
    switch (action.type) {
        case 'clear':
            return null;
        case 'set':
            return action.payload;
        case 'updateQuery':
            return (state && __assign(__assign({}, state), { query: JSON.stringify(action.payload), queryObject: action.payload }));
        /* istanbul ignore next */
        default:
            console.error('Invalid action type for selectedPortFilter');
            return null;
    }
};
/**
 * Hook to manage the selected port filter
 * - Validates filter from smartgroups.
 */
export function useSelectedPortFilter() {
    var data = usePortSmartgroups().data;
    var _a = __read(useReducer(selectedPortFilterReducer, null), 2), _selectedPortFilter = _a[0], dispatchSelectedPortFilter = _a[1];
    /**
     The selected filter must be verified before passing down to context.
     This is to prevent the selected filter from being out of "sync" if the filter is deleted or changed.
     */
    var selectedPortFilter = useMemo(function () { return verifySelectedPortFilter(_selectedPortFilter, data === null || data === void 0 ? void 0 : data.smartgroups); }, [_selectedPortFilter, data === null || data === void 0 ? void 0 : data.smartgroups]);
    return {
        selectedPortFilter: selectedPortFilter,
        dispatchSelectedPortFilter: dispatchSelectedPortFilter,
    };
}
/**
 * Port Filter Context Provider
 */
export function PortFilterContextProvider(_a) {
    var children = _a.children, filter = _a.filter, setFilter = _a.setFilter, permissions = _a.permissions;
    var _b = useSelectedPortFilter(), selectedPortFilter = _b.selectedPortFilter, dispatchSelectedPortFilter = _b.dispatchSelectedPortFilter;
    var isSelectedFilterApplied = useMemo(function () { return checkSelectedFilterApplied(selectedPortFilter, filter); }, [selectedPortFilter, filter]);
    var contextValue = useMemo(function () { return ({
        filter: filter,
        setFilter: setFilter,
        selectedFilter: selectedPortFilter,
        dispatchSelectedFilter: dispatchSelectedPortFilter,
        isSelectedFilterApplied: isSelectedFilterApplied,
        permissions: permissions,
    }); }, [dispatchSelectedPortFilter, filter, isSelectedFilterApplied, permissions, selectedPortFilter, setFilter]);
    return _jsx(PortFilterContext.Provider, __assign({ value: contextValue }, { children: children }));
}
